import { computed, ref, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFocusMode } from 'dashboard/composables/useFocusMode';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import { PRIMARY_SEGMENT_KEYS } from 'dashboard/components/widgets/conversation/conversationQuickFilters';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { conversationViewComparator } from 'dashboard/store/modules/conversations/helpers';
import wootConstants from 'dashboard/constants/globals';

export const FOCUS_QUEUE_FILTERS = PRIMARY_SEGMENT_KEYS;

const boundConversations = shallowRef([]);
const boundApplyFilter = ref(null);
const boundActiveKey = ref('mine');
const skippedIds = ref([]);

export function bindFocusConversationQueue({
  conversations = [],
  applyFilter = null,
  activeKey = '',
} = {}) {
  boundConversations.value = Array.isArray(conversations) ? conversations : [];
  boundApplyFilter.value =
    typeof applyFilter === 'function' ? applyFilter : null;
  if (activeKey) boundActiveKey.value = activeKey;
}

export function waitingMinutesFrom(waitingSince) {
  if (!waitingSince) return 0;
  const raw = Number(waitingSince);
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  const ms = raw < 1e12 ? raw * 1000 : raw;
  return Math.max(0, Math.floor((Date.now() - ms) / 60000));
}

function contactNameOf(chat) {
  return chat?.meta?.sender?.name || chat?.meta?.sender?.email || '';
}

// `autopilot` só na instância dona da tela (ConversationView): o watch abaixo
// navega sozinho, e cada componente que chama este composable registraria uma
// cópia dele — 5 router.push pro mesmo destino no mesmo tick.
export function useFocusConversationQueue({ autopilot = false } = {}) {
  const store = useStore();
  const router = useRouter();
  const route = useRoute();
  const { t } = useI18n();
  const { accountId } = useAccount();
  const { isListHidden, setListMode } = useWorkbenchLayout();
  const { disableFocusMode } = useFocusMode();

  const currentChat = computed(() => store.getters.getSelectedChat || {});
  const activeFilterKey = computed(() => boundActiveKey.value || 'mine');

  const sortedQueue = computed(() => {
    const comparator = conversationViewComparator('waiting');
    const ordered = [...boundConversations.value].sort(comparator);
    const skipped = [];
    const rest = [];
    ordered.forEach(chat => {
      if (skippedIds.value.includes(chat.id)) skipped.push(chat);
      else rest.push(chat);
    });
    return [...rest, ...skipped];
  });

  const total = computed(() => sortedQueue.value.length);
  const isQueueEmpty = computed(() => isListHidden.value && total.value === 0);

  const currentIndex = computed(() => {
    const id = currentChat.value?.id;
    if (!id) return -1;
    return sortedQueue.value.findIndex(chat => chat.id === id);
  });

  const position = computed(() =>
    currentIndex.value >= 0 ? currentIndex.value + 1 : 0
  );

  const progressValue = computed(() => {
    if (!total.value) return 0;
    if (!position.value) return 0;
    return Math.round((position.value / total.value) * 100);
  });

  const nextConversation = computed(() => {
    if (total.value <= 1) return null;
    const list = sortedQueue.value;
    if (currentIndex.value < 0) return list[0];
    return list[(currentIndex.value + 1) % list.length];
  });

  const canNavigate = computed(
    () => Boolean(nextConversation.value) && total.value > 1
  );

  const nextName = computed(() => contactNameOf(nextConversation.value));

  const nextPosition = computed(() => {
    if (!nextConversation.value || !total.value) return 0;
    if (currentIndex.value < 0) return 1;
    return ((currentIndex.value + 1) % total.value) + 1;
  });

  const currentWaitingMinutes = computed(() =>
    waitingMinutesFrom(currentChat.value?.waiting_since)
  );

  const currentInbox = computed(() => {
    const inboxId = currentChat.value?.inbox_id;
    if (!inboxId) return null;
    return store.getters['inboxes/getInbox'](inboxId) || null;
  });

  const conversationTypeFromRoute = () => {
    const name = route.name;
    if (name === 'conversation_through_mentions') return 'mention';
    if (name === 'conversation_through_participating') return 'participating';
    if (name === 'conversation_through_unattended') return 'unattended';
    return '';
  };

  const conversationPath = chat => {
    if (!chat?.id) return '';
    return frontendURL(
      conversationUrl({
        accountId: accountId.value,
        activeInbox: route.params.inbox_id,
        id: chat.id,
        label: route.params.label,
        teamId: route.params.teamId,
        conversationType: conversationTypeFromRoute(),
        foldersId:
          route.name === 'conversations_through_folders'
            ? route.params.id
            : undefined,
      })
    );
  };

  const goToConversation = chat => {
    const path = conversationPath(chat);
    if (!path || route.path === path) return;
    router.push(path);
  };

  const goNext = () => {
    if (!canNavigate.value) return;
    goToConversation(nextConversation.value);
  };

  const skipForNow = () => {
    const id = currentChat.value?.id;
    if (!id || !canNavigate.value) return;
    const upcoming = nextConversation.value;
    if (!skippedIds.value.includes(id)) {
      skippedIds.value = [...skippedIds.value, id];
    }
    goToConversation(upcoming);
  };

  const finalizeCurrent = async () => {
    const id = currentChat.value?.id;
    if (!id) return;
    const upcoming = nextConversation.value;
    try {
      await store.dispatch('toggleStatus', {
        conversationId: id,
        status: wootConstants.STATUS_TYPE.RESOLVED,
      });
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
      skippedIds.value = skippedIds.value.filter(skippedId => skippedId !== id);
      if (upcoming && upcoming.id !== id) {
        goToConversation(upcoming);
      }
    } catch (error) {
      useAlert(t('CONVERSATION.CHANGE_STATUS_FAILED'));
    }
  };

  const setQueueFilter = key => {
    if (!FOCUS_QUEUE_FILTERS.includes(key)) return;
    boundApplyFilter.value?.(key);
  };

  const exitFocus = () => {
    disableFocusMode();
  };

  const exitToList = () => {
    setListMode('list');
  };

  if (autopilot) {
    watch(
      [isListHidden, sortedQueue, () => currentChat.value?.id],
      ([hidden, queue, currentId]) => {
        if (!hidden || !queue.length) return;
        if (currentId && queue.some(chat => chat.id === currentId)) return;
        goToConversation(queue[0]);
      }
    );
  }

  return {
    isListHidden,
    isQueueEmpty,
    activeFilterKey,
    sortedQueue,
    total,
    position,
    progressValue,
    nextConversation,
    nextName,
    nextPosition,
    canNavigate,
    currentWaitingMinutes,
    currentInbox,
    currentChat,
    goNext,
    skipForNow,
    finalizeCurrent,
    setQueueFilter,
    exitFocus,
    exitToList,
  };
}
